/**
 * Themed DatePicker — calendar grid that matches the app's surface tokens.
 * Replaces native <input type="date"> (which looks out-of-place in dark mode
 * and renders a locale-dependent format). Displays the selected value as
 * DD/MM/YYYY regardless of browser locale; the wire format stays YYYY-MM-DD.
 *
 * Controlled component:
 *   value: 'YYYY-MM-DD' or ''
 *   onChange: (newValue: 'YYYY-MM-DD' | '') => void
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';

export interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  todayLabel?: string;
  clearLabel?: string;
}

const WEEKDAY_KEYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function toDate(iso: string): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function toIso(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDisplay(iso: string): string {
  const d = toDate(iso);
  if (!d) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export function DatePicker({
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
  className = '',
  id,
  todayLabel = 'Today',
  clearLabel = 'Clear',
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  // The month currently being viewed; starts on the selected date or today.
  const [viewMonth, setViewMonth] = useState<Date>(() => toDate(value) ?? new Date());
  const rootRef = useRef<HTMLDivElement>(null);
  const autoId = useMemo(() => id || `dp-${Math.random().toString(36).slice(2, 8)}`, [id]);

  useEffect(() => {
    if (open) setViewMonth(toDate(value) ?? new Date());
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const selectedIso = value;
  const todayIso = useMemo(() => toIso(new Date()), []);

  // Build 6 x 7 day grid — starts on Monday, fills from previous month as needed.
  const days = useMemo(() => {
    const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    // Monday = 0, Sunday = 6.
    const firstWeekday = (first.getDay() + 6) % 7;
    const start = new Date(first);
    start.setDate(start.getDate() - firstWeekday);

    const cells: { date: Date; iso: string; inMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push({
        date: d,
        iso: toIso(d),
        inMonth: d.getMonth() === viewMonth.getMonth(),
      });
    }
    return cells;
  }, [viewMonth]);

  const goPrev = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  const goNext = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));

  const pick = (iso: string) => {
    onChange(iso);
    setOpen(false);
  };
  const pickToday = () => {
    onChange(todayIso);
    setOpen(false);
  };
  const clear = () => {
    onChange('');
    setOpen(false);
  };

  return (
    <div className={`w-full ${className}`} ref={rootRef}>
      {label && (
        <label htmlFor={autoId} className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        <button
          id={autoId}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${
            open
              ? 'border-[var(--accent-primary)] bg-[var(--surface-raised)] ring-2 ring-[var(--accent-primary)]/25'
              : 'border-[var(--border-subtle)] bg-[var(--surface-raised)] hover:border-[var(--border-default)]'
          }`}
        >
          <span
            className={`flex-1 min-w-0 truncate ${
              value ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'
            }`}
          >
            {value ? formatDisplay(value) : placeholder ?? 'DD/MM/YYYY'}
          </span>
          <CalendarIcon className="w-4 h-4 text-[var(--text-muted)] flex-shrink-0" />
        </button>

        {open && (
          <div className="absolute z-30 left-0 top-full mt-1 w-72 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-base)] shadow-xl p-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous month"
                className="p-1.5 rounded-md text-[var(--text-secondary)] hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="text-sm font-semibold text-[var(--text-primary)]">
                {MONTH_NAMES[viewMonth.getMonth()]} {viewMonth.getFullYear()}
              </div>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next month"
                className="p-1.5 rounded-md text-[var(--text-secondary)] hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)] transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Weekday row */}
            <div className="grid grid-cols-7 mb-1">
              {WEEKDAY_KEYS.map((w) => (
                <div key={w} className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] text-center py-1">
                  {w}
                </div>
              ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7 gap-0.5">
              {days.map(({ date, iso, inMonth }) => {
                const isSelected = iso === selectedIso;
                const isToday = iso === todayIso;
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => pick(iso)}
                    className={`h-8 w-full rounded-md text-xs font-medium transition-colors ${
                      isSelected
                        ? 'bg-[var(--accent-primary)] text-[var(--accent-text)]'
                        : isToday
                          ? 'bg-[var(--accent-primary)]/15 text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/25'
                          : inMonth
                            ? 'text-[var(--text-primary)] hover:bg-[var(--surface-raised)]'
                            : 'text-[var(--text-muted)] hover:bg-[var(--surface-raised)]'
                    }`}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="mt-3 pt-2 border-t border-[var(--border-subtle)] flex items-center justify-between">
              <button
                type="button"
                onClick={pickToday}
                className="px-2 py-1 text-xs font-medium rounded-md text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/10 transition-colors"
              >
                {todayLabel}
              </button>
              <button
                type="button"
                onClick={clear}
                className="px-2 py-1 text-xs font-medium rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-raised)] transition-colors"
              >
                {clearLabel}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DatePicker;
